import { useEffect, useState } from 'react';
import { Page } from './Page';
import { useNav, useDateIntent, useWindowIntent, type DateWindow } from './NavContext';
import { BookingsView } from '../components/bookings';
import { useBookings } from '../components/bookings/useBookings';
import { NATURAL_DIR, EMPTY_BOOKINGS_FILTER } from '../components/bookings/constants';
import type { BookingsFilter, SortKey, SortState } from '../components/bookings/types';

// Default: most-recent-first = "date added" desc. Applied server-side so a new
// booking lands at the top of page one, not buried.
const DEFAULT_SORT: SortState = { key: 'added', dir: 'desc' };

// THE ARRIVAL FROM DAILY (viewings-in-the-bookings-list AC11): the filter is
// REPLACED with the All baseline plus the window — not merged, as the status
// intent is. A page left on last month's "In on" date would otherwise land on
// All with the stale date still excluding every row the panel listed (frame.md,
// landmine 9). Sort is not part of the filter and is left alone.
export function landWithWindow(_current: BookingsFilter, window: DateWindow): BookingsFilter {
  return { ...EMPTY_BOOKINGS_FILTER, window };
}

// THE IN-RESIDENCE DAY IS A BOOKINGS QUESTION (codex, PR #181): "In residence →
// view all" lands on Type: Booking with the day, because only the bookings read
// carries `inOn` — under All the chip would show a day the mixed read was not
// honouring. Status is cleared, as before; the window cannot coexist with it.
export function landInOn(f: BookingsFilter, date: string): BookingsFilter {
  return { ...f, types: ['booking'], status: null, inOn: date, window: null };
}

// Bookings section — a table of all bookings (table-only; no calendar/charts, so
// no date control). Owns its filter + sort; chrome comes from <Page>.
export function BookingsPage() {
  const [filter, setFilter] = useState<BookingsFilter>(EMPTY_BOOKINGS_FILTER);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const handleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: NATURAL_DIR[key] ?? 'asc' },
    );
  const { state, totals, page, setPage, reload } = useBookings(filter, sort);

  // A status filter handed over by another page (e.g. a legacy "view all" that
  // arrives with status 'arrived'): apply it, then clear the one-shot.
  const { statusIntent, consumeStatusIntent } = useNav();
  useEffect(() => {
    if (!statusIntent) return;
    setFilter((f) => ({ ...f, status: statusIntent as BookingsFilter['status'] }));
    consumeStatusIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusIntent]);

  // A day handed over by "In residence → view all": show the dogs in residence
  // on that day (occupancy), clearing any status so it's the full in-house set.
  useDateIntent('bookings', (date) => setFilter((f) => landInOn(f, date)));

  // The window handed over by Daily's "Open in Bookings": replace, never merge.
  useWindowIntent('bookings', (window) => setFilter((f) => landWithWindow(f, window)));

  return (
    <Page title="Bookings">
      <BookingsView
        state={state}
        filter={filter}
        setFilter={setFilter}
        sort={sort}
        onSort={handleSort}
        totals={totals}
        page={page}
        setPage={setPage}
        onRetry={reload}
      />
    </Page>
  );
}
